import { FirebaseError } from 'firebase/app';

export interface RetryOptions {
    maxRetries?: number;
    initialDelay?: number;
    maxDelay?: number;
    backoffMultiplier?: number;
}

export class FirestoreRetryError extends Error {
    constructor(
        message: string,
        public originalError: FirebaseError,
        public attempts: number
    ) {
        super(message);
        this.name = 'FirestoreRetryError';
    }
}

/**
 * Retry a Firestore operation with exponential backoff
 */
export async function retryFirestoreOperation<T>(
    operation: () => Promise<T>,
    options: RetryOptions = {}
): Promise<T> {
    const {
        maxRetries = 3,
        initialDelay = 1000,
        maxDelay = 10000,
        backoffMultiplier = 2
    } = options;

    let lastError: FirebaseError;
    let delay = initialDelay;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            return await operation();
        } catch (error) {
            lastError = error as FirebaseError;

            // Check if this is a retryable error
            if (!isRetryableError(lastError)) {
                throw new FirestoreRetryError(
                    `Non-retryable error after ${attempt} attempts: ${lastError.message}`,
                    lastError,
                    attempt
                );
            }

            // If this was the last attempt, throw the error
            if (attempt === maxRetries) {
                throw new FirestoreRetryError(
                    `Operation failed after ${maxRetries} attempts: ${lastError.message}`,
                    lastError,
                    attempt
                );
            }

            // Log the retry attempt
            console.warn(`Firestore operation failed (attempt ${attempt}/${maxRetries}):`, {
                error: lastError.message,
                code: lastError.code,
                delay: delay
            });

            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, delay));

            // Increase delay for next attempt (exponential backoff)
            delay = Math.min(delay * backoffMultiplier, maxDelay);
        }
    }

    // This should never be reached, but TypeScript requires it
    throw new FirestoreRetryError(
        'Unexpected error in retry logic',
        lastError!,
        maxRetries
    );
}

/**
 * Check if a Firestore error is retryable
 */
export function isRetryableError(error: FirebaseError): boolean {
    const retryableCodes = [
        'resource-exhausted',           // Write stream exhausted
        'unavailable',                  // Service unavailable
        'deadline-exceeded',            // Request timeout
        'internal',                     // Internal server error
        'aborted',                      // Request aborted
        'cancelled',                    // Request cancelled
        'data-loss',                    // Data loss
        'unknown'                       // Unknown error
    ];

    return retryableCodes.includes(error.code);
}

/**
 * Check if the error is specifically a write queue exhaustion error
 */
export function isWriteQueueExhaustedError(error: FirebaseError): boolean {
    return error.code === 'resource-exhausted' &&
        error.message.includes('Write stream exhausted');
}

/**
 * Get recommended action based on error type
 */
export function getRecommendedAction(error: FirebaseError): string {
    if (isWriteQueueExhaustedError(error)) {
        return 'Write queue is full. Consider batching writes or reducing write frequency.';
    }

    switch (error.code) {
        case 'unavailable':
            return 'Service temporarily unavailable. Retry with exponential backoff.';
        case 'deadline-exceeded':
            return 'Request timed out. Consider reducing data size or retrying.';
        case 'permission-denied':
            return 'Permission denied. Check Firestore security rules.';
        case 'not-found':
            return 'Document not found. Verify the document path.';
        case 'already-exists':
            return 'Document already exists. Use update() instead of set().';
        default:
            return 'Unknown error. Check Firebase documentation.';
    }
} 